import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Home, Trees, Goal, Dumbbell, Crown } from "lucide-react";
import { useMember } from "../MemberContext";
import { Brand } from "../TrainingShell";
import { api } from "../api";
import { H1, H2, Overline } from "../ui";

const HERO = "https://images.unsplash.com/photo-1544698310-74ea9d1c8258?w=2000&q=80&fm=jpg";
const U = (id) => `https://images.unsplash.com/photo-${id}?w=1000&q=80&fm=jpg`;
const CAT_IMG = {
  technik: U("1574629810360-7efbbe195018"),
  koordination: U("1601646721773-8a73c6739bcf"),
  stabilitaet: U("1714646442330-9068099f5521"),
  kraft: U("1618787113620-fc01359c1c78"),
  ausdauer: U("1776465960036-918a03931b03"),
  torwart: U("1597630907602-356d09900200"),
  regeneration: U("1602520628350-fbf9db1f02ae"),
};
const TIERS = [
  { id: "free", name: "Kostenlos", items: ["Ausgewählte Übungen", "Tägliche Motivation", "Einblick in die Bibliothek", "Vorschau auf Premium-Inhalte"] },
  { id: "premium", name: "Premium", items: ["Komplette Trainingsbibliothek", "Individuelle Wochenpläne", "Erweiterte Fortschrittsübersicht", "Ernährungsbereich", "Challenges & ausgewählte Live-Trainings"], hl: true },
  { id: "premium_plus", name: "Premium Plus", items: ["Alles aus Premium", "Zusätzliche Trainingspläne", "Exklusive Challenges", "Besondere Live-Einheiten"] },
];

function Hero({ member }) {
  return (
    <section className="relative min-h-[88vh] flex items-end overflow-hidden" data-testid="landing-hero">
      <img src={HERO} alt="Fußballspieler beim Training" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/20" />
      <div className="relative max-w-7xl mx-auto w-full px-5 lg:px-10 pb-20 pt-40">
        <Overline className="mb-5">Die digitale Trainingsplattform</Overline>
        <H1 className="max-w-4xl">Trainiere wie ein <span className="text-[#c1ff72]">Profi.</span><br />Wo und wann du willst.</H1>
        <p className="mt-6 max-w-xl text-base sm:text-lg text-[#A1A1AA] font-light leading-relaxed">Zuhause, im Garten, im Park oder auf dem Platz – mit und ohne Material. Für Kinder, Jugendliche und Erwachsene, vom Hobbykicker bis zum Leistungsspieler.</p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to={member ? "/training/start" : "/training/registrieren"} data-testid="hero-cta-register"
            className="inline-flex items-center gap-2 bg-[#c1ff72] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#a8eb54] transition-colors active:scale-95">
            {member ? "Zum Training" : "Kostenlos starten"}<ArrowRight className="w-5 h-5" />
          </Link>
          {!member && <Link to="/training/login" data-testid="hero-cta-login" className="inline-flex items-center px-8 py-4 border border-white/25 text-white uppercase tracking-wider hover:border-white transition-colors">Anmelden</Link>}
        </div>
      </div>
    </section>
  );
}

function Places() {
  const P = [[Home, "Zuhause", "Wenig Platz, ohne Material"], [Trees, "Garten & Park", "Ball und ein paar Hütchen"], [Goal, "Fußballplatz", "Technik, Schuss, Torwart"], [Dumbbell, "Fitnessraum", "Kraft und Athletik"]];
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-24" data-testid="landing-places">
      <div className="grid grid-cols-2 lg:grid-cols-4 border-l border-t border-white/10">
        {P.map(([Icon, t, d]) => (
          <div key={t} className="p-8 border-r border-b border-white/10"><Icon className="w-7 h-7 text-[#c1ff72] mb-6" /><p className="font-['Bebas_Neue'] text-2xl text-white">{t}</p><p className="text-sm text-[#71717A] mt-1">{d}</p></div>
        ))}
      </div>
    </section>
  );
}

function Categories({ meta, counts }) {
  const cats = meta?.categories || [];
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-24" data-testid="landing-categories">
      <Overline className="mb-3">Trainingsbibliothek</Overline>
      <H2 className="mb-10 max-w-2xl">Sieben Bereiche. Ein Ziel: besser werden.</H2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 auto-rows-[160px] md:auto-rows-[200px]">
        {cats.map((c, i) => (
          <Link key={c.id} to={`/training/bibliothek?kategorie=${c.id}`} data-testid={`landing-cat-${c.id}`}
            className={`group relative overflow-hidden border border-white/10 bg-[#121212] p-5 flex flex-col justify-end ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
            {CAT_IMG[c.id] && <img src={CAT_IMG[c.id]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:scale-105 transition-transform duration-500" />}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
            <div className="relative"><p className="font-['Bebas_Neue'] text-2xl md:text-3xl text-white leading-none">{c.label}</p>
              <p className="text-xs text-[#A1A1AA] mt-1 uppercase tracking-wider">{counts[c.id] || 0} Übungen</p></div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Tiers() {
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-28" data-testid="landing-tiers">
      <Overline className="mb-3">Mitgliedschaft</Overline>
      <H2 className="mb-3">Starte kostenlos. Wachse mit Premium.</H2>
      <p className="text-sm text-[#71717A] mb-10">Preise und Leistungen findest du nach der kostenlosen Anmeldung unter „Mitgliedschaft“. Zahlung per Karte, SEPA-Lastschrift oder PayPal.</p>
      <div className="grid md:grid-cols-3 gap-4">
        {TIERS.map((t) => (
          <div key={t.id} className={`p-8 border ${t.hl ? "border-[#c1ff72] bg-[#c1ff72]/[0.04]" : "border-white/10 bg-[#121212]"}`} data-testid={`tier-card-${t.id}`}>
            <div className="flex items-center gap-2 mb-6">{t.id !== "free" && <Crown className={`w-5 h-5 ${t.id === "premium_plus" ? "text-[#FFD700]" : "text-[#c1ff72]"}`} />}<p className="font-['Bebas_Neue'] text-3xl text-white">{t.name}</p></div>
            <ul className="space-y-3">{t.items.map((x) => <li key={x} className="flex gap-3 text-sm text-[#A1A1AA]"><Check className="w-4 h-4 text-[#c1ff72] shrink-0 mt-0.5" />{x}</li>)}</ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  const { member, meta } = useMember();
  const [counts, setCounts] = useState({});
  useEffect(() => {
    api("/exercises").then((d) => {
      const c = {};
      d.items.forEach((e) => e.categories.forEach((k) => { c[k] = (c[k] || 0) + 1; }));
      setCounts(c);
    }).catch(() => {});
  }, []);
  return (
    <div className="bg-[#0A0A0A] text-white font-['Outfit']" data-testid="training-landing">
      <header className="absolute top-0 inset-x-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-20 flex items-center justify-between"><Brand />
          <Link to={member ? "/training/start" : "/training/login"} className="text-sm uppercase tracking-wider text-white hover:text-[#c1ff72]" data-testid="landing-header-login">{member ? "Mein Training" : "Anmelden"}</Link>
        </div>
      </header>
      <Hero member={member} />
      <Places />
      <Categories meta={meta} counts={counts} />
      <Tiers />
      <footer className="border-t border-white/10 py-10 text-center text-xs text-[#71717A]">
        <Link to="/" className="hover:text-white">personal-fussballtraining.de</Link> · <Link to="/impressum" className="hover:text-white">Impressum</Link> · <Link to="/datenschutz" className="hover:text-white">Datenschutz</Link>
      </footer>
    </div>
  );
}
